import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Bell, Check, CheckCheck, Package, CreditCard, Truck, Link2, Info, AlertTriangle, ShieldAlert, Building2, MessageSquare } from 'lucide-react';

const typeIcons = {
  connection: Link2, stock: Package, payment: CreditCard, delivery: Truck,
  order: ShieldAlert, alert: AlertTriangle, info: Info,
  workspace_new: Building2, ticket_new: MessageSquare,
};
const typeColors = {
  connection: 'text-blue-500', stock: 'text-emerald-500', payment: 'text-amber-500',
  delivery: 'text-violet-500', order: 'text-rose-500', alert: 'text-orange-500', info: 'text-slate-500',
  workspace_new: 'text-indigo-500', ticket_new: 'text-sky-500',
};
const typeLabels = {
  all: 'All', connection: 'Links', stock: 'Stock', payment: 'Pay',
  delivery: 'Delivery', order: 'Order', alert: 'Alert', info: 'Info',
  workspace_new: 'Workspace', ticket_new: 'Ticket',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(dateStr).toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const ref = useRef(null);
  const navigate = useNavigate();

  // Poll unread count
  const fetchCount = useCallback(async () => {
    try {
      const { data } = await getUnreadCount();
      setCount(data.count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifications = async () => {
    if (loaded) return;
    try {
      const { data } = await getNotifications(10);
      setNotifications(data);
      setLoaded(true);
    } catch { /* ignore */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleMarkRead = async (e, notif) => {
    e.stopPropagation();
    if (notif.read) return;
    await markNotificationRead(notif.notification_id);
    setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
    setCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setCount(0);
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await markNotificationRead(notif.notification_id);
      setCount(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    setLoaded(false);
    if (notif.link) navigate(notif.link);
  };

  const goToAll = () => {
    setOpen(false);
    setLoaded(false);
    navigate('/notifications');
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" onClick={toggle} className="relative" data-testid="notification-bell-btn">
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center" data-testid="notification-count-badge">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <span className="text-sm font-semibold">Notifications</span>
            {count > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="dropdown-mark-all-read">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Type filter chips */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-border/50 overflow-x-auto" data-testid="notification-type-filters">
            {Object.entries(typeLabels).map(([key, label]) => {
              const isActive = typeFilter === key;
              const typeCount = key === 'all' ? notifications.length : notifications.filter(n => n.type === key).length;
              if (key !== 'all' && typeCount === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setTypeFilter(key)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors
                    ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                  data-testid={`filter-type-${key}`}
                >
                  {key !== 'all' && (() => { const Icon = typeIcons[key] || Info; return <Icon className="w-2.5 h-2.5" />; })()}
                  {label}
                  {typeCount > 0 && <span className="opacity-70">({typeCount})</span>}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {(() => {
              const filtered = typeFilter === 'all' ? notifications : notifications.filter(n => n.type === typeFilter);
              if (filtered.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <Bell className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-xs">{typeFilter === 'all' ? 'No notifications' : `No ${typeLabels[typeFilter]} notifications`}</p>
                  </div>
                );
              }
              return filtered.map(notif => {
                const Icon = typeIcons[notif.type] || Info;
                const color = typeColors[notif.type] || typeColors.info;
                return (
                  <div
                    key={notif.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-accent/50 transition-colors border-b border-border/50 last:border-0 ${!notif.read ? 'bg-primary/[0.03]' : ''}`}
                    onClick={() => handleClick(notif)}
                    data-testid={`dropdown-notif-${notif.notification_id}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-xs font-medium leading-tight flex-1 ${!notif.read ? '' : 'text-muted-foreground'}`}>{notif.title}</p>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${typeColors[notif.type]?.replace('text-', 'bg-').replace('500', '100')} ${typeColors[notif.type] || ''}`}>
                          {typeLabels[notif.type] || notif.type}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-muted-foreground/50 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <button onClick={(e) => handleMarkRead(e, notif)} className="shrink-0 p-1 rounded hover:bg-accent mt-0.5" title="Mark read">
                        <Check className="w-3 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-2.5 bg-muted/30">
            <button onClick={goToAll} className="text-xs text-primary hover:underline w-full text-center" data-testid="view-all-notifications-btn">
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
